// ─────────────────────────────────────────────────────────────────────────
// IngresoBodega — ingreso rápido de stock por código (26-ago-2026).
//
// Diseñado para la persona EN LA BODEGA digitando desde el celular:
// escribe el código (SKU con o sin espacios, o parte del nombre) → elige la
// pieza → cantidad con botones grandes → REGISTRAR → el foco vuelve al
// código para seguir con la siguiente. Cada registro queda auditado en
// stockMovements (quién, cuánto, antes/después) y se puede deshacer al tiro.
// En escritorio es el mismo flujo, en tarjeta compacta.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState, useCallback } from "react";
import backend from "../backend";
import useIsMobile from "../hooks/useIsMobile";

const C = {
  card:  { background: "#242424", border: "1px solid #2e2e2e", borderRadius: 10, padding: 14 },
  label: { fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6, display: "block" },
  btnBig: (bg) => ({
    minHeight: 52, borderRadius: 10, border: "none", background: bg, color: "#fff",
    fontSize: 17, fontWeight: 700, cursor: "pointer", width: "100%", letterSpacing: ".02em",
  }),
  step: {
    width: 56, height: 52, borderRadius: 10, border: "1px solid #3a3a3a", background: "#161616",
    color: "#fff", fontSize: 26, fontWeight: 700, cursor: "pointer", flexShrink: 0,
  },
};

const fmtHora = (d) => new Date(d).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" });

const IngresoBodega = ({ onStockChange }) => {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [pieza, setPieza] = useState(null);       // pieza seleccionada
  const [qty, setQty] = useState(1);
  const [modo, setModo] = useState("sumar");      // sumar | fijar
  const [nota, setNota] = useState("");
  const [showNota, setShowNota] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [sesion, setSesion] = useState([]);       // movimientos registrados en esta sesión
  const [aviso, setAviso] = useState(null);       // { tipo, texto }
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Búsqueda con debounce mientras tipea.
  useEffect(() => {
    if (pieza) return;                             // ya eligió: no buscar
    if (q.trim().length < 2) { setResultados([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await backend.get("stock/buscar", { params: { q } });
        setResultados(res.data?.resultados || []);
      } catch { setResultados([]); }
      setBuscando(false);
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [q, pieza]);

  const avisar = (tipo, texto) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 3500);
  };

  const elegir = (p) => {
    setPieza(p);
    setResultados([]);
    setQty(1);
    setModo("sumar");
  };

  const limpiar = useCallback(() => {
    setPieza(null); setQ(""); setQty(1); setNota(""); setShowNota(false); setModo("sumar");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const registrar = async () => {
    if (!pieza || guardando) return;
    const n = Number(qty);
    if (!Number.isFinite(n) || (modo === "sumar" && n === 0)) { avisar("error", "Revisa la cantidad"); return; }
    setGuardando(true);
    try {
      const res = await backend.post("stock/ingreso", {
        ...(pieza.tipo === "material" ? { materialId: pieza._id } : { pieceId: pieza._id }),
        qty: n, modo, nota,
      });
      const mov = res.data?.movimiento;
      setSesion(prev => [{ ...mov, _stockFinal: res.data?.stock }, ...prev].slice(0, 25));
      onStockChange?.(pieza._id, res.data?.stock, pieza.tipo || "pieza");
      try { navigator.vibrate?.(60); } catch { /* sin vibración */ }
      avisar("ok", `${pieza.sku || pieza.name}: stock ${res.data?.stock}`);
      limpiar();
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
    setGuardando(false);
  };

  const deshacer = async (mov) => {
    try {
      const res = await backend.post("stock/deshacer", { movimientoId: mov._id });
      setSesion(prev => prev.map(m => m._id === mov._id ? { ...m, revertido: true } : m));
      onStockChange?.(mov.piece || mov.material, res.data?.stock, mov.piece ? "pieza" : "material");
      avisar("ok", `Deshecho: ${mov.sku || mov.pieceName} vuelve a ${res.data?.stock}`);
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
  };

  // Enter en el buscador con 1 solo resultado = elegirlo directo.
  const onKeyBuscar = (e) => {
    if (e.key === "Enter" && resultados.length >= 1) elegir(resultados[0]);
  };

  return (
    <div className="bodega-card" style={{ ...C.card, marginBottom: 18, maxWidth: isMobile ? "100%" : 620 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>📦 Ingreso rápido de bodega</div>
        {aviso && (
          <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
            {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
          </span>
        )}
      </div>

      {!pieza && (
        <>
          <label style={C.label}>Código o nombre de la pieza</label>
          <input
            ref={inputRef}
            className="bini-input"
            style={{ fontSize: 18, minHeight: 52, width: "100%", boxSizing: "border-box" }}
            placeholder="Ej: 3222 3280 58 (con o sin espacios)"
            value={q}
            autoFocus={!isMobile}
            enterKeyHint="search"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyBuscar}
          />
          {buscando && <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>Buscando…</div>}
          {!buscando && q.trim().length >= 2 && resultados.length === 0 && (
            <div style={{ fontSize: 12, color: "#f39c12", marginTop: 8 }}>
              Sin resultados — revisa el código, o crea la pieza desde "+ Producto externo" más abajo.
            </div>
          )}
          {resultados.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {resultados.map((r) => (
                <button
                  key={r._id}
                  type="button"
                  onClick={() => elegir(r)}
                  className="t-02 tap-card bodega-opcion"
                  style={{
                    textAlign: "left", background: "#161616", border: "1px solid #333", borderRadius: 8,
                    padding: "12px 12px", color: "#ddd", cursor: "pointer", minHeight: 52,
                  }}
                >
                  {r.tipo === "material" && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#3498db", border: "1px solid #1a4a6e", borderRadius: 4, padding: "1px 5px", marginRight: 6 }}>MATERIAL</span>
                  )}
                  <span style={{ fontFamily: "ui-monospace, Consolas, monospace", color: "#e2b96f", fontSize: 14 }}>
                    {r.sku || "(sin SKU)"}
                  </span>
                  <span style={{ margin: "0 8px", color: "#666" }}>·</span>
                  <span style={{ fontSize: 13 }}>{r.name}</span>
                  <span style={{ float: "right", fontSize: 12, color: "#999" }}>stock {r.stock ?? 0}{r.unit ? ` ${r.unit}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {pieza && (
        <>
          <div className="bodega-opcion" style={{ background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "10px 12px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "ui-monospace, Consolas, monospace", color: "#e2b96f", fontSize: 15 }}>
                {pieza.tipo === "material" && <span style={{ fontSize: 10, fontWeight: 700, color: "#3498db", border: "1px solid #1a4a6e", borderRadius: 4, padding: "1px 5px", marginRight: 6 }}>MATERIAL</span>}
                {pieza.sku || "(sin SKU)"}
              </div>
              <div style={{ fontSize: 13, color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pieza.name}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 11, color: "#999" }}>stock actual</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{pieza.stock ?? 0}</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button type="button" onClick={() => setModo("sumar")} className="t-02"
              style={{ ...C.btnBig(modo === "sumar" ? "#e30613" : "#2a2a2a"), minHeight: 40, fontSize: 13 }}>
              ➕ Sumar al stock
            </button>
            <button type="button" onClick={() => { setModo("fijar"); setQty(pieza.stock ?? 0); }} className="t-02"
              style={{ ...C.btnBig(modo === "fijar" ? "#e30613" : "#2a2a2a"), minHeight: 40, fontSize: 13 }}>
              📋 Conteo (fijar)
            </button>
          </div>

          <label style={C.label}>{modo === "fijar" ? "Stock real contado" : "Cantidad que ingresa"}</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <button type="button" className="t-02" style={C.step} onClick={() => setQty(v => Math.max(modo === "fijar" ? 0 : -999, Number(v || 0) - 1))}>−</button>
            <input
              className="bini-input"
              type="number"
              inputMode="numeric"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onFocus={(e) => e.target.select()}
              style={{ fontSize: 26, fontWeight: 700, textAlign: "center", minHeight: 52, width: "100%", boxSizing: "border-box" }}
            />
            <button type="button" className="t-02" style={C.step} onClick={() => setQty(v => Number(v || 0) + 1)}>+</button>
          </div>

          {showNota ? (
            <input
              className="bini-input"
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 12, minHeight: 44 }}
              placeholder="Nota (opcional): de dónde viene, guía, etc."
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          ) : (
            <button type="button" onClick={() => setShowNota(true)}
              style={{ background: "none", border: "none", color: "#888", fontSize: 12, cursor: "pointer", marginBottom: 12, padding: 0 }}>
              + agregar nota
            </button>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={limpiar} className="t-02"
              style={{ ...C.btnBig("#2a2a2a"), width: 110, flexShrink: 0, fontSize: 14 }}>
              Cancelar
            </button>
            <button type="button" onClick={registrar} disabled={guardando} className="t-02" style={C.btnBig(guardando ? "#7a1a1f" : "#e30613")}>
              {guardando ? "Registrando…" : modo === "fijar" ? "✓ FIJAR CONTEO" : "✓ REGISTRAR INGRESO"}
            </button>
          </div>
        </>
      )}

      {sesion.length > 0 && (
        <div style={{ marginTop: 14, borderTop: "1px solid #2e2e2e", paddingTop: 10 }}>
          <div style={{ ...C.label, marginBottom: 8 }}>Registrados en esta sesión</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sesion.map((m) => (
              <div key={m._id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: m.revertido ? "#666" : "#ccc", textDecoration: m.revertido ? "line-through" : "none" }}>
                <span style={{ color: "#888", flexShrink: 0 }}>{fmtHora(m.createdAt)}</span>
                <span style={{ fontFamily: "ui-monospace, Consolas, monospace", color: "#e2b96f", flexShrink: 0 }}>{m.sku || "—"}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{m.pieceName}</span>
                <span style={{ marginLeft: "auto", fontWeight: 700, color: m.delta >= 0 ? "#2ecc71" : "#e74c3c", flexShrink: 0 }}>
                  {m.modo === "fijar" ? `=${m.despues}` : (m.delta >= 0 ? `+${m.delta}` : m.delta)}
                </span>
                {!m.revertido && m.modo !== "reversa" && (
                  <button type="button" onClick={() => deshacer(m)}
                    style={{ background: "none", border: "1px solid #444", borderRadius: 6, color: "#999", fontSize: 11, cursor: "pointer", padding: "4px 8px", flexShrink: 0, minHeight: 30 }}>
                    deshacer
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Bitácora de movimientos (colapsable) ─────────────────────────────────────
// Muestra los últimos movimientos de stock registrados (quién, cuándo, cuánto)
// desde la colección stockMovements. Carga al expandir para no pesar la vista.
export const MovimientosRecientes = () => {
  const [abierto, setAbierto] = useState(false);
  const [movs, setMovs] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await backend.get("stock/movimientos", { params: { limit: 40 } });
      setMovs(res.data?.movimientos || []);
    } catch { setMovs([]); }
    setCargando(false);
  };

  const abrir = () => {
    setAbierto(v => !v);
    if (!abierto && movs === null) cargar();
  };

  const fmtF = (d) => new Date(d).toLocaleString("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const MODO = { sumar: "➕ ingreso", fijar: "📋 conteo", reversa: "↩ reversa" };

  return (
    <div className="bodega-card" style={{ ...C.card, marginBottom: 18, maxWidth: 620, padding: abierto ? 14 : "8px 14px" }}>
      <button type="button" onClick={abrir}
        style={{ background: "none", border: "none", color: "#cbd5e1", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, width: "100%", textAlign: "left", minHeight: 32 }}>
        📜 Movimientos de stock recientes {abierto ? "▾" : "▸"}
      </button>
      {abierto && (
        <div style={{ marginTop: 10 }}>
          {cargando && <div style={{ fontSize: 12, color: "#999" }}>Cargando…</div>}
          {movs && movs.length === 0 && <div style={{ fontSize: 12, color: "#999" }}>Sin movimientos registrados aún.</div>}
          {movs && movs.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 340, overflowY: "auto" }}>
              {movs.map((m) => (
                <div key={m._id} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12, color: m.revertido ? "#555" : "#ccc", textDecoration: m.revertido ? "line-through" : "none", borderBottom: "1px solid #1f1f1f", paddingBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ color: "#888", flexShrink: 0 }}>{fmtF(m.createdAt)}</span>
                  <span style={{ color: "#777", fontSize: 11, flexShrink: 0 }}>{MODO[m.modo] || m.modo}</span>
                  {m.material && <span style={{ fontSize: 9, fontWeight: 700, color: "#3498db", flexShrink: 0 }}>MAT</span>}
                  <span style={{ fontFamily: "ui-monospace, Consolas, monospace", color: "#e2b96f", flexShrink: 0 }}>{m.sku || "—"}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 60, flex: 1 }}>{m.pieceName}</span>
                  <span style={{ fontWeight: 700, color: m.delta >= 0 ? "#2ecc71" : "#e74c3c", flexShrink: 0 }}>
                    {m.modo === "fijar" ? `=${m.despues}` : (m.delta >= 0 ? `+${m.delta}` : m.delta)}
                  </span>
                  <span style={{ color: "#666", fontSize: 11, flexShrink: 0 }}>({m.antes}→{m.despues})</span>
                  <span style={{ color: "#5a7a9a", fontSize: 11, flexShrink: 0 }}>{(m.porNombre || "").split("@")[0]}</span>
                  {m.nota && <span style={{ color: "#777", fontSize: 11, width: "100%" }}>📝 {m.nota}</span>}
                </div>
              ))}
            </div>
          )}
          <button type="button" onClick={cargar} style={{ marginTop: 8, background: "none", border: "1px solid #333", borderRadius: 6, color: "#999", fontSize: 11, cursor: "pointer", padding: "5px 10px" }}>
            ↻ actualizar
          </button>
        </div>
      )}
    </div>
  );
};

export default IngresoBodega;
